import React, { useContext, useEffect, useState } from 'react';
import { Paper, Tab, Tabs } from '@material-ui/core';

import { logisticsApi as api } from 'api/logistics';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

const CROSS_DOCK_FEES_FEATURE_FLAG = 'CROSS_DOCK_FEES';

function ViewInboundCrossDocks({ canEdit, canOffsite, crossDockFeesEnabled }) {
    const [crossDocks, setCrossDocks] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });

    const getCrossDocks = async () => {
        const response = await api.getInboundCrossDocks(true);
        if (!response.status) {
            setNotification({ text: 'Failed to get xDocks/Offsites', level: 'error' });
            setCrossDocks([]);
            return;
        }

        const processedData = response.data.rows.map((row) => ({
            ...row,
            typeWithFC: row.offsite ? `Offsite (${row.facilityName})` : 'XDock',
        }));
        setCrossDocks(processedData);
    };

    useEffect(() => {
        getCrossDocks();
    }, []);

    const columns = [
        { accessor: 'name', Header: 'Name', editable: canEdit },
        { accessor: 'lineOne', Header: 'Address', editable: canEdit },
        { accessor: 'city', Header: 'City', editable: canEdit },
        { accessor: 'province', Header: 'State', editable: canEdit },
        {
            accessor: 'zip',
            Header: 'Zip',
            editable: canEdit,
            editProps: { type: 'input', inputType: 'number', min: '0' },
        },
        { accessor: 'contactInfo', Header: 'Contact Info', editable: canEdit },
        {
            accessor: 'typeWithFC',
            Header: 'Type (Main FC)',
            editable: false,
        },
        { accessor: 'netsuiteId', Header: 'Netsuite ID' },
        {
            accessor: 'qcEnabled',
            Header: 'QC?',
            type: 'checkbox',
            editable: (row) => canEdit && canOffsite && row.original.offsite,
            editProps: { type: 'checkbox' },
        },
    ];

    if (crossDockFeesEnabled) {
        columns.push({
            accessor: 'serviceFee',
            Header: 'Service Fee',
            editable: canEdit,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        });
    }

    return (
        <div>
            <h1>View Inbound Cross Docks</h1>
            <Notifications options={notification} />
            <br />
            <DataTable
                columns={columns}
                data={crossDocks}
                editable
                saveEdit={async ({ field, id, row, value }) => {
                    const item = {};
                    item[field] = value;

                    const res = await api.updateInboundCrossDocks(id, item);
                    if (res.status === false) {
                        setNotification({ text: 'Failed to update xDock/Offsite', level: 'error' });
                    } else {
                        setNotification({ text: 'xDock/Offsite modified!', level: 'success' });
                        getCrossDocks();
                    }
                }}
            />
        </div>
    );
}

function CreateInboundCrossDocks({ canOffsite, crossDockFeesEnabled }) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [isOffsite, setIsOffsite] = useState(false);
    const [fcOptions, setFcOptions] = useState([]);

    useEffect(() => {
        const getFcOptions = async () => {
            const response = await warehouseApi.getFacilities(true);
            if (response.status) {
                setFcOptions(
                    response.data.rows.map((fc) => ({
                        text: fc.name,
                        value: fc.id,
                    })),
                );
            }
        };
        getFcOptions();
    }, []);

    const formFields = [
        {
            name: 'name',
            inputElement: 'textField',
            label: 'Name',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'lineOne',
            inputElement: 'textField',
            label: 'Address',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'city',
            inputElement: 'textField',
            label: 'City',
            gridValue: 3,
            inputProps: { required: true },
        },
        {
            name: 'province',
            inputElement: 'textField',
            label: 'State',
            gridValue: 1,
            inputProps: { required: true },
        },
        {
            name: 'zip',
            inputElement: 'textField',
            label: 'Zip',
            gridValue: 4,
            inputProps: { type: 'number', integer: true, max: '99999', required: true },
        },
        {
            name: 'contactInfo',
            inputElement: 'textField',
            label: 'Contact Info',
            gridValue: 6,
        },
    ];

    const serviceFeeFields = [
        {
            name: 'serviceFee',
            inputElement: 'textField',
            label: 'Service Fee',
            gridValue: 1,
            inputProps: { type: 'number', decimal: true, required: false },
        },
    ];

    if (crossDockFeesEnabled) {
        serviceFeeFields.forEach((field) => {
            formFields.push(field);
        });
    }

    const offsiteFields = [
        {
            name: 'facilityId',
            inputElement: 'select',
            label: 'Main FC',
            gridValue: 2,
            inputProps: {
                required: true,
                opts: fcOptions,
            },
            display: () => isOffsite,
            dependencies: ['offsite'],
        },
        {
            name: 'qcEnabled',
            inputElement: 'checkbox',
            label: 'QC?',
            gridValue: 2,
            initialValue: false,
            display: () => isOffsite,
            dependencies: ['offsite'],
        },
    ];

    if (canOffsite) {
        formFields.push({
            name: 'offsite',
            inputElement: 'checkbox',
            label: 'Offsite',
            gridValue: 2,
            initialValue: isOffsite,
            inputProps: {
                onChange: (event, isChecked) => {
                    setIsOffsite(isChecked);
                },
            },
        });
    }

    // Combine base fields with offsite fields
    const allFormFields = [...formFields, ...offsiteFields];

    return (
        <div>
            <h1>Create Inbound Cross Docks</h1>
            <Notifications options={notification} />
            <br />
            <FormComponent
                formFields={allFormFields}
                onSubmit={async (formData, resetForm) => {
                    if (!canOffsite) {
                        formData.offsite = false;
                    }
                    const res = await api.createInboundCrossDocks(formData);
                    if (res.status === false) {
                        setNotification({ text: `Unable to create xDock/Offsite ${res.msg}`, level: 'error' });
                        return;
                    }

                    setNotification({ text: 'Success!', level: 'success' });
                    resetForm();
                    setIsOffsite(false);
                }}
            />
        </div>
    );
}

function InboundCrossDocks() {
    const [selectedTab, setSelectedTab] = useState('0');
    const { data: crossDockFeesEnabled } = useIsFeatureOn(CROSS_DOCK_FEES_FEATURE_FLAG);
    const { roles } = useContext(UserContext);
    const canEdit = roles?.filter((role) => role.name.endsWith('CREATE_INBOUND_CROSS_DOCKS')).length >= 1;
    const canOffsite = roles?.some((role) => role.name === 'OFFSITE_TRANSFERS');

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    return (
        <Paper>
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab id="ViewInboundCrossDocks_Tab" label="View Inbound Cross Docks" value="0" />
                {canEdit && <Tab id="CreateInboundCrossDocks_Tab" label="Create Inbound Cross Docks" value="1" />}
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <ViewInboundCrossDocks
                    canEdit={canEdit}
                    canOffsite={canOffsite}
                    crossDockFeesEnabled={crossDockFeesEnabled}
                />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <CreateInboundCrossDocks canOffsite={canOffsite} crossDockFeesEnabled={crossDockFeesEnabled} />
            </TabPanel>
        </Paper>
    );
}

export { InboundCrossDocks };
